import { useState, useRef, useEffect } from 'react';
import useApi from '../../Api';
import { useAuth } from '../../AuthContext';
import { toast } from 'react-toastify';

const useGuideInteraction = (guide_id, initialGuideData) => {
  const apiClient = useApi();
  const { token } = useAuth();
  const [userInteraction, setUserInteraction] = useState(initialGuideData ? initialGuideData.user_interaction : null);
  const [likeCount, setLikeCount] = useState(initialGuideData ? initialGuideData.like_count : 0);
  const [dislikeCount, setDislikeCount] = useState(initialGuideData ? initialGuideData.dislike_count : 0);
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [error, setError] = useState(null);
  const interactionLoadingRef = useRef(false);
  useEffect(() => {
    if (initialGuideData) {
      setUserInteraction(initialGuideData.user_interaction);
      setLikeCount(initialGuideData.like_count);
      setDislikeCount(initialGuideData.dislike_count);
    }
  }, [initialGuideData]);
  const likeGuide = async () => {
    if (interactionLoadingRef.current) return;
    interactionLoadingRef.current = true;
    setInteractionLoading(true);
    setError(null);
    let previousInteraction = userInteraction;
    if (previousInteraction === 'like') {
      setLikeCount((prev) => Math.max(prev - 1, 0));
      setUserInteraction(null);
    } else {
      setLikeCount((prev) => prev + 1);
      if (previousInteraction === 'dislike') {
        setDislikeCount((prev) => Math.max(prev - 1, 0));
      }
      setUserInteraction('like');
    }
    try {
      await apiClient.post(`/guides/${guide_id}/like`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.error('Error liking the guide:', err);
      toast.error(err.response && err.response.data && err.response.data.detail ? err.response.data.detail : 'An unexpected error occurred while liking the guide.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      if (previousInteraction === 'like') {
        setLikeCount((prev) => prev + 1);
        setUserInteraction('like');
      } else {
        setLikeCount((prev) => Math.max(prev - 1, 0));
        if (previousInteraction === 'dislike') {
          setDislikeCount((prev) => prev + 1);
        }
        setUserInteraction(previousInteraction);
      }
      if (err.response && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('An unexpected error occurred while liking the guide.');
      }
    } finally {
      interactionLoadingRef.current = false;
      setInteractionLoading(false);
    }
  };
  const dislikeGuide = async () => {
    if (interactionLoadingRef.current) return;
    interactionLoadingRef.current = true;
    setInteractionLoading(true);
    setError(null);
    let previousInteraction = userInteraction;
    if (previousInteraction === 'dislike') {
      setDislikeCount((prev) => Math.max(prev - 1, 0));
      setUserInteraction(null);
    } else {
      setDislikeCount((prev) => prev + 1);
      if (previousInteraction === 'like') {
        setLikeCount((prev) => Math.max(prev - 1, 0));
      }
      setUserInteraction('dislike');
    }
    try {
      await apiClient.post(`/guides/${guide_id}/dislike`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.error('Error disliking the guide:', err);
      toast.error(err.response && err.response.data && err.response.data.detail ? err.response.data.detail : 'An unexpected error occurred while disliking the guide.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      if (previousInteraction === 'dislike') {
        setDislikeCount((prev) => prev + 1);
        setUserInteraction('dislike');
      } else {
        setDislikeCount((prev) => Math.max(prev - 1, 0));
        if (previousInteraction === 'like') {
          setLikeCount((prev) => prev + 1);
        }
        setUserInteraction(previousInteraction);
      }
      if (err.response && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('An unexpected error occurred while disliking the guide.');
      }
    } finally {
      interactionLoadingRef.current = false;
      setInteractionLoading(false);
    }
  };
  return {
    userInteraction,
    likeCount,
    dislikeCount,
    interactionLoading,
    error,
    likeGuide,
    dislikeGuide,
  };
};
export default useGuideInteraction;
